import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { SessionManager as Session } from "../utils/sessionManager";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  customHeaders?: Record<string, string>,
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(data ? { "Content-Type": "application/json" } : {}),
    ...(customHeaders || {}),
  };

  // Incluir token de autenticação automaticamente
  const token = Session.getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // Se for 401, limpar tokens inválidos antes de lançar erro
  if (res.status === 401) {
    console.log('🚫 Token inválido detectado, limpando sessão');
    Session.clearSession();
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      // Incluir token de autenticação automaticamente
      const token = Session.getToken();

      const headers: Record<string, string> = {
        ...(token && { "Authorization": `Bearer ${token}` }),
      };

      const res = await fetch(queryKey.join("/") as string, {
        headers,
        credentials: "include",
      });

      // Se for 401, limpar tokens inválidos
      if (res.status === 401) {
        Session.clearSession();

        if (unauthorizedBehavior === "returnNull") {
          return null;
        }
      }

      await throwIfResNotOk(res);
      return await res.json();
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Remover queryFn padrão para permitir que cada hook defina sua própria função
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
      retry: 1, // Permitir 1 retry
    },
    mutations: {
      retry: false,
    },
  },
});


